import type { ResolveIngestError } from '@/features/SalesforceIngest/api/resolveIngestHooks'
import { Alert } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** `null` until the server has rejected a submission. */
  error: ResolveIngestError | null
}

/**
 * The inline 400 / 503, above the submit button.
 *
 * Inline rather than a toast: the operator has a field to fix and needs the
 * message to stay put while they fix it. This matches
 * `SALESFORCE_INLINE_STATUSES` in the sibling accounts feature.
 *
 * `role` is explicit, following `SalesforceLinkFormDialog`: Chakra v3's
 * `Alert.Root` renders a plain div with no implicit role, and this alert only
 * ever appears *after* a failed submit, so without it a rejection is
 * announced to nobody.
 *
 * The subtle second line carries `ProblemDetailResponse.correlationId` when
 * the server sent one. That is the value an operator pastes into a bug report.
 */
export function SubmitErrorAlert({ error }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  if (!error) {
    return null
  }

  return (
    <Alert.Root status="error" role="alert">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>
          {String(t('salesforceIngest.form.submitFailed'))}
        </Alert.Title>
        <Alert.Description>
          {error.detail ??
            String(t('salesforceIngest.form.submitFailedFallback'))}
        </Alert.Description>
        {error.correlationId ? (
          <Alert.Description fontSize="xs" color="text.text-subtle">
            {`${String(t('salesforceIngest.form.correlationId'))} ${
              error.correlationId
            }`}
          </Alert.Description>
        ) : null}
      </Alert.Content>
    </Alert.Root>
  )
}
